import { Icon, ISelectOption, SearchBolder, TableColumn, TableRow, Tooltip } from '@aon/aon-tws-common-ui';
import { CounterDisplayWithToggle, QuestionVariationRow, ResponseOptionsTooltipContainer } from '@aon/tws-common-ui-questions';
import {
	ILibraryQuestion,
	ILibraryQuestion as ICommonLibraryQuestion,
	IQuestionVariation,
} from '@aon/tws-common-ui-questions/build/lib/models';
import { ColumnHelper } from '@aon/tws-common-ui-questions/build/lib/utilities';
import * as React from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { IColumn, IExternalAttributeVm } from '../../../models';

const Tag = styled.div.attrs({
	className: 'tws-question-tag',
})`
	 {
		float: left;
		border-radius: 3px;
		padding: 5px;
		margin: 3px;
		background-color: #666;
		color: #fff;
	}
`;
Tag.displayName = 'Tag';

export interface IDemographicQuestionRowParams {
	question: ICommonLibraryQuestion;
	disabled: boolean;
	isVariationDisabled: boolean;
	columns: IColumn[];
	searchText?: string;
	selectedQuestionVariationIds: number[];
	surveySupportedCultures: string[];
	availableCultures: ISelectOption[];
	mappedAttribute?: IExternalAttributeVm;
	SelectQuestion: (question: ICommonLibraryQuestion, isSelected: boolean) => void;
	SelectQuestionVariation: (question: ILibraryQuestion, questionVariation: IQuestionVariation, isSelected: boolean) => void;
	ShowSingleVariation: (questionId: number) => void;
}

export interface IDemographicQuestionRowState {
	visibleColumns: IColumn[];
}

export class DemographicQuestionRow extends React.PureComponent<IDemographicQuestionRowParams, IDemographicQuestionRowState> {
	constructor(props: IDemographicQuestionRowParams) {
		super(props);

		this.state = {
			visibleColumns: ColumnHelper.GetSelectedColumns(this.props.columns),
		};

		this.GetMissingLanguages = this.GetMissingLanguages.bind(this);
	}

	public static getDerivedStateFromProps(props: IDemographicQuestionRowParams, state: IDemographicQuestionRowState) {
		const visibleColumns: IColumn[] = ColumnHelper.GetSelectedColumns(props.columns);
		return { visibleColumns };
	}

	public render() {
		const isSelected = this.props.question.questionVariationId
			? this.props.selectedQuestionVariationIds.indexOf(this.props.question.questionVariationId) > -1
			: false;

		return (
			<>
				<TableRow
					selected={isSelected}
					disabled={this.props.disabled}
					selectable={true}
					onSelectRow={() => this.props.SelectQuestion(this.props.question, !isSelected)}
					key={this.props.question.code}
					record={this.props.question}
				>
					{_(this.state.visibleColumns)
						.orderBy('displayOrder')
						.map((column: IColumn) => this.getQuestionPropertyText(column, isSelected))
						.value()}
				</TableRow>
				{this.GetQuestionVariationRows()}
			</>
		);
	}

	private GetQuestionVariationRows = () => {
		if (
			this.props.question.showVariations === false ||
			this.props.question.questionVariations === undefined ||
			!this.props.question.questionVariations.length
		) {
			return null;
		}

		return _.map(this.props.question.questionVariations, (questionVariation: IQuestionVariation, index) => (
			<QuestionVariationRow
				{...this.props}
				selectable={true}
				supportedCultures={this.props.surveySupportedCultures}
				QuestionVariation={questionVariation}
				SelectQuestionVariation={(isSelected: boolean) =>
					this.props.SelectQuestionVariation(this.props.question, questionVariation, isSelected)
				}
				isSelected={this.props.selectedQuestionVariationIds.indexOf(questionVariation.questionVariationId) > -1}
				variationActionMenu={[]}
				key={index}
				disabled={this.props.disabled && this.props.selectedQuestionVariationIds.indexOf(questionVariation.questionVariationId) <= -1}
			/>
		));
	};

	private getTagsContent() {
		if (!this.props.question.tagNames || !this.props.question.tagNames.length) {
			return <span />;
		}

		return this.props.question.tagNames.map((tag: string, index: number) => <Tag key={index}>{tag}</Tag>);
	}

	private getAlternateVersionsContent = () => {
		const variationCount: number = this.props.question.alternateVersions;
		return (
			<>
				<CounterDisplayWithToggle
					count={variationCount}
					onClick={this.props.ShowSingleVariation}
					id={this.props.question.questionId}
					open={this.props.question.showVariations}
				/>
			</>
		);
	};

	// NOTE: The property name and the column name are not always the same.
	// Please use the enum column name values for switch statement
	private getQuestionPropertyText(column: IColumn, isSelected: boolean) {
		const { question } = this.props;
		const key = this.props.question.questionId + '_' + column.name.toLowerCase();
		switch (column.name) {
			case 'code': {
				const code = this.props.question.code;
				return (
					<TableColumn key={key}>
						<SearchBolder text={code} searchText={this.props.searchText} />
					</TableColumn>
				);
			}
			case 'text': {
				const text = this.props.question.text;
				return (
					<TableColumn key={key}>
						<SearchBolder text={text} searchText={this.props.searchText} />
					</TableColumn>
				);
			}
			case 'alternateVersions':
				return <TableColumn key={key}>{this.getAlternateVersionsContent()}</TableColumn>;
			case 'tags':
				return <TableColumn key={key}>{this.getTagsContent()}</TableColumn>;
			case 'responseOptionGroup':
				return (
					<TableColumn key={key}>
						<ResponseOptionsTooltipContainer
							id={question.questionId}
							invertedTooltip={isSelected}
							questionType={question.questionType}
							responseOptionGroup={question.responseOptionGroup}
							responseOptions={question.responseOptions}
						/>
					</TableColumn>
				);
			case 'mappedAttribute':
				return <TableColumn key={key}>{this.props.mappedAttribute !== undefined && this.props.mappedAttribute.displayName}</TableColumn>;
			case 'allChosenLanguages': {
				const missingLanguages = this.GetMissingLanguages();
				const tooltipLangId = question.questionId + '_allChosenLanguages';
				return (
					<TableColumn key={key}>
						{missingLanguages.length === 0 ? (
							<Icon name='yes' />
						) : (
							<>
								{missingLanguages.length + ' missing '}
								<Tooltip id={tooltipLangId} iconName='error' contentType='jsx' inverted={isSelected}>
									<div>
										<p>
											<strong>{ContentConstants.MISSING_LANGUAGES_TOOLTIP_TITLE_TEXT}</strong>
										</p>
										<ul>
											{missingLanguages.map((id, liKey) => {
												const culture = this.props.availableCultures.find((c) => c.name === id);
												return <li key={liKey}>{culture ? culture.displayText : id}</li>;
											})}
										</ul>
										<br />
										{ContentConstants.MISSING_LANGUAGES_TOOLTIP_REMINDER_TEXT}
									</div>
								</Tooltip>
							</>
						)}
					</TableColumn>
				);
			}
			case 'sourceType':
				return <TableColumn key={key}>{ColumnHelper.GetSourceTypeContent(question.sourceType)}</TableColumn>;
		}
	}

	private GetMissingLanguages(): string[] {
		if (this.props.question.languages) {
			return this.props.surveySupportedCultures.filter((id) => this.props.question.languages.indexOf(id) === -1);
		}
		return [];
	}
}
